import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { dtrService } from "../../api/dailyTimeRecords";
import Badge from "../../components/ui/Badge";
import { getErrorMessage } from "../../utils/apiError";
import { formatDate } from "../../utils/date";
import { formatManilaDateTime, todayManilaKey } from "../../utils/manilaTime";
import { getEmployeeId } from "../../utils/session";
import { cx } from "../../utils/classNames";
import LocationsModal from "../attendance/components/LocationsModal";
import {
  DTR_QUERY_KEY,
  canRequestFix,
  getCutoffStatus,
  periodKeyOf,
} from "./dtrUtils";
import { CutoffSummary } from "./components/DtrTotals";
import DtrDaysTable from "./components/DtrDaysTable";
import { useMyProfile } from "../profile/useMyProfile";
import { isDeployed } from "../corrections/correctionUtils";
import RequestCorrectionModal from "../corrections/RequestCorrectionModal";
import MyCorrections from "../corrections/MyCorrections";
import ui from "../../components/ui/ui.module.css";
import styles from "./Dtr.module.css";

// URL: ?year=&month=&cutoff= → that cut-off; nothing → the current one.
const usePeriodParam = () => {
  const [params, setParams] = useSearchParams();
  const year = Number(params.get("year"));
  const month = Number(params.get("month"));
  const cutoff = Number(params.get("cutoff"));
  const period =
    year && month >= 1 && month <= 12 && [0, 1, 2, 3].includes(cutoff)
      ? { year, month, cutoff }
      : null;
  return {
    period,
    open: (p) =>
      setParams(
        p?.isCurrent ? {} : { year: p.year, month: p.month, cutoff: p.cutoff },
      ),
  };
};

/**
 * My DTR: one cut-off as a day list. There is no submit: HR approves each
 * employee's cut-off, then exports it (exported = locked). Past cut-offs
 * come from the dropdown and are view-only; Fix asks for a correction.
 */
const MyDtrView = () => {
  const hasEmployee = !!getEmployeeId();
  const { period, open } = usePeriodParam();
  const [dialog, setDialog] = useState(null); // { locations: id } | { correct: { date, attendanceId } }
  // Deployed employees can't request corrections: HR fixes their days
  const deployed = isDeployed(useMyProfile().data);
  const todayKey = todayManilaKey();

  const periodsQuery = useQuery({
    queryKey: [DTR_QUERY_KEY, "my-periods"],
    queryFn: dtrService.getMyPeriods,
    enabled: hasEmployee,
    staleTime: 5 * 60 * 1000,
  });
  const detailQuery = useQuery({
    queryKey: [DTR_QUERY_KEY, "my-period", periodKeyOf(period) || "current"],
    queryFn: () => dtrService.getMyPeriod(period || {}),
    enabled: hasEmployee,
  });

  if (!hasEmployee) {
    return (
      <div className={cx(ui.alert, ui.alertDanger)}>
        Your account is not linked to an employee record, so DTR is not
        available.
      </div>
    );
  }

  const detail = detailQuery.data;
  const periods = periodsQuery.data?.periods ?? [];
  const selectedKey = periodKeyOf(detail?.period || period);
  const status = detail && getCutoffStatus(detail.status);
  const readOnly = detail && (!detail.isCurrent || detail.locked);

  return (
    <div className={ui.page}>
      <div className={ui.pageHeader}>
        <div>
          <h4 className="page-title">My DTR</h4>
          <p className="page-subtitle">
            Your days for the cut-off. Nothing to submit: HR approves your
            cut-off and sends it to payroll. If a day is wrong, press Fix to ask
            for a correction.
          </p>
        </div>
        <div className={ui.actions}>
          <Link to="/me/overtime" className={cx(ui.button, ui.secondary)}>
            <i className="bi bi-stopwatch"></i>
            My Overtime
          </Link>
        </div>
      </div>

      {/* Cut-off: picker | totals | status */}
      <div
        className={cx(
          ui.card,
          ui.cardPadded,
          ui.pageHeader,
          styles.cutoffHeader,
        )}
      >
        <div>
          <label htmlFor="dtr-period" className={ui.label}>
            Cut-off
          </label>
          <select
            id="dtr-period"
            className={ui.input}
            value={selectedKey}
            onChange={(e) => {
              const next = periods.find(
                (p) => periodKeyOf(p) === e.target.value,
              );
              if (next) open(next);
            }}
          >
            {!periods.some((p) => periodKeyOf(p) === selectedKey) && (
              <option value={selectedKey}>
                {detail?.period?.label ?? "Loading…"}
              </option>
            )}
            {periods.map((p) => (
              <option key={periodKeyOf(p)} value={periodKeyOf(p)}>
                {p.label}
                {p.isCurrent ? " (current)" : ""} ·{" "}
                {getCutoffStatus(p.status).label}
              </option>
            ))}
          </select>
        </div>

        {detail?.totals && (
          <div className={styles.cutoffHeaderSummary}>
            <CutoffSummary
              totals={detail.totals}
              title={`Cut-off total${detail.period?.label ? ` · ${detail.period.label}` : ""}`}
            />
          </div>
        )}

        {status && (
          <div className="d-flex flex-column gap-1">
            <div className="d-flex flex-wrap align-items-center gap-2">
              <Badge tone={status.tone}>{status.label}</Badge>
              {detail.processOn && (
                <span className={ui.hint}>
                  Process by {formatDate(detail.processOn)}
                </span>
              )}
            </div>
            {detail.status === "APPROVED" && detail.approvedAt && (
              <span className={ui.hint}>
                {detail.approvedByName || "HR"} ·{" "}
                {formatManilaDateTime(detail.approvedAt)}
              </span>
            )}
            {detail.status === "EXPORTED" && detail.exportedAt && (
              <span className={ui.hint}>
                Processed for payroll {formatManilaDateTime(detail.exportedAt)}
              </span>
            )}
            {detail.reopened && detail.status !== "EXPORTED" && (
              <span className={cx(ui.hint, "text-warning-emphasis")}>
                <i className="bi bi-unlock me-1"></i>
                Reopened by {detail.reopened.byName || "HR"} on{" "}
                {formatManilaDateTime(detail.reopened.at)}
                {detail.reopened.reason ? `: ${detail.reopened.reason}` : ""}
              </span>
            )}
            {detail.changedAfterApproval && (
              <span className={cx(ui.hint, "text-warning-emphasis")}>
                Changed after HR approved it: HR will check it again.
              </span>
            )}
            {readOnly && (
              <span className={ui.hint}>
                <i className="bi bi-eye me-1"></i>
                {detail.locked
                  ? "Processed: sent to payroll, view only."
                  : "Past cut-off: view only."}
              </span>
            )}
          </div>
        )}
      </div>

      {detailQuery.isLoading ? (
        <div className={cx(ui.card, ui.empty)}>
          <span
            className="spinner-border spinner-border-sm"
            role="status"
            aria-hidden="true"
          ></span>
          Loading your DTR…
        </div>
      ) : detailQuery.isError ? (
        <div className={cx(ui.alert, ui.alertDanger)}>
          {getErrorMessage(
            detailQuery.error,
            "Unable to load your DTR for this cut-off.",
          )}
        </div>
      ) : (
        <>
          <MyCorrections />

          <div className={ui.card}>
            <div className={ui.cardHeader}>
              <span className={ui.cardTitle}>
                Days · {detail.period?.label}
              </span>
            </div>
            <DtrDaysTable
              days={detail.days}
              onViewLocations={(id) => setDialog({ locations: id })}
              renderAction={
                deployed
                  ? undefined
                  : (day) =>
                      !readOnly &&
                      canRequestFix(day, {
                        locked: detail.locked,
                        todayKey,
                      }) &&
                      day.status !== "REST_DAY" &&
                      day.status !== "HOLIDAY" && (
                        <button
                          type="button"
                          className={cx(
                            ui.button,
                            ui.secondary,
                            ui.buttonSmall,
                          )}
                          onClick={() =>
                            setDialog({
                              correct: {
                                date: day.date,
                                attendanceId: day.attendanceId,
                              },
                            })
                          }
                          title="Ask your approver to fix this day"
                        >
                          <i className="bi bi-pencil"></i> Fix
                        </button>
                      )
              }
            />
          </div>
          {deployed && (
            <p className={ui.hint}>
              Deployed employees: ask HR to fix a wrong day.
            </p>
          )}
        </>
      )}

      {dialog?.correct && (
        <RequestCorrectionModal
          date={dialog.correct.date}
          attendanceId={dialog.correct.attendanceId}
          onClose={() => setDialog(null)}
        />
      )}
      {dialog?.locations && (
        <LocationsModal
          attendanceId={dialog.locations}
          onClose={() => setDialog(null)}
        />
      )}
    </div>
  );
};

export default MyDtrView;
